import * as T from 'three';
import { stylize } from './stylize';

/**
 * Sculptural, instanced foliage (Bible §7 "grouped leaf clusters", §10 "instancing, clustered
 * leaves, aggressive LODs, shared materials"). Every mesh carries authored volume normals and a
 * teal-shadow → sunlit-yellow-green vertex gradient; instance colours set the hue.
 */
export type FoliageKind = 'clump' | 'clumpB' | 'fern' | 'flower' | 'hang' | 'palm' | 'far' | 'bromeliad' | 'giant' | 'lily' | 'beard';

type Builder = { p: number[]; n: number[]; c: number[] };
const builder = (): Builder => ({ p: [], n: [], c: [] });
function vert(b: Builder, p: T.Vector3, n: T.Vector3, c: T.Color) {
    b.p.push(p.x, p.y, p.z);
    b.n.push(n.x, n.y, n.z);
    b.c.push(c.r, c.g, c.b);
}
function geometry(b: Builder) {
    const g = new T.BufferGeometry();
    g.setAttribute('position', new T.Float32BufferAttribute(b.p, 3));
    g.setAttribute('normal', new T.Float32BufferAttribute(b.n, 3));
    g.setAttribute('color', new T.Float32BufferAttribute(b.c, 3));
    g.computeBoundingSphere();
    g.computeBoundingBox();
    return g;
}
function rng(seed: number) {
    return () => { seed |= 0; seed = seed + 0x6D2B79F5 | 0; let t = Math.imul(seed ^ seed >>> 15, 1 | seed); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; };
}
const BOTTOM = new T.Color(.40, .54, .57), MID = new T.Color(.84, .9, .8), TOP = new T.Color(1.1, 1.08, .84);
/** Height gradient shared by all foliage: teal shadow at the base, warm sunlit top. */
function shade(h: number, out: T.Color) {
    h = Math.min(1, Math.max(0, h));
    return h < .55 ? out.copy(BOTTOM).lerp(MID, h / .55) : out.copy(MID).lerp(TOP, (h - .55) / .45);
}
const RADII = new T.Vector3(1, .66, 1);
const ellipsoidNormal = (p: T.Vector3, out: T.Vector3) => out.set(p.x / RADII.x ** 2, p.y / RADII.y ** 2, p.z / RADII.z ** 2).normalize();

/**
 * A leaf clump: a lumpy ellipsoid core shingled with broad leaves whose tips curl out, so the
 * silhouette scallops like the concept art while shading stays one soft, readable mass.
 */
function clumpGeometry(seed: number, petals: number, leafScale = 1, core = true) {
    const random = rng(seed), b = builder();
    const p = new T.Vector3(), n = new T.Vector3(), c = new T.Color(), e = new T.Vector3();
    if (core) {
        const ico = new T.IcosahedronGeometry(.86, 1), pos = ico.getAttribute('position');
        const bump = (x: number, y: number, z: number) => 1 + .09 * Math.sin(x * 5.1 + seed) * Math.sin(z * 4.3 + y * 3.7 + seed * .7);
        for (let i = 0; i < pos.count; i++) {
            p.fromBufferAttribute(pos, i);
            p.multiplyScalar(bump(p.x, p.y, p.z)).multiply(RADII);
            if (p.y < -.32) p.y = -.32 + (p.y + .32) * .45; // flatter, shaded underside
            ellipsoidNormal(p, n);
            shade((p.y / RADII.y + 1) / 2 * .9, c);
            vert(b, p, n, c);
        }
        ico.dispose();
    }
    const down = new T.Vector3(0, -1, 0), t = new T.Vector3(), side = new T.Vector3(), base = new T.Vector3(), along = new T.Vector3();
    const rows = 3;
    for (let i = 0; i < petals; i++) {
        const y = 1 - (i + .5) / petals * 1.42, r = Math.sqrt(Math.max(0, 1 - y * y)), phi = i * 2.39996 + random() * .4;
        const dir = new T.Vector3(Math.cos(phi) * r, y, Math.sin(phi) * r);
        const s = dir.clone().multiply(RADII).multiplyScalar(.84);
        t.copy(down).addScaledVector(dir, -dir.dot(down));
        if (t.lengthSq() < .04) t.set(Math.cos(phi + 1.3), 0, Math.sin(phi + 1.3));
        t.normalize();
        side.crossVectors(dir, t).normalize();
        const len = (.72 + random() * .28) * leafScale, width = (.5 + random() * .14) * leafScale, curl = .16 + random() * .1;
        base.copy(s).addScaledVector(t, -.28 * leafScale).addScaledVector(dir, -.06);
        const tone = .92 + random() * .14;
        const grid: T.Vector3[][] = [];
        for (let k = 0; k <= rows; k++) {
            // Rounded leaf: broad shoulders, blunt tip (soft scalloped silhouette, not spikes).
            const u = k / rows, w = width / 2 * Math.max(.18, Math.sin(Math.PI * (.12 + .8 * u)) ** .45);
            along.copy(base).addScaledVector(t, len * u).addScaledVector(dir, curl * u * u);
            grid.push([-1, 0, 1].map(j => along.clone().addScaledVector(side, j * w).addScaledVector(dir, .05 * (1 - Math.abs(j)) - .015 * Math.abs(j))));
        }
        const emit = (q: T.Vector3, u: number) => {
            ellipsoidNormal(q, e);
            n.copy(e).multiplyScalar(.9).addScaledVector(dir, .1).normalize();
            shade((q.y / RADII.y + 1) / 2, c).multiplyScalar(tone * (1 + .1 * u));
            vert(b, q, n, c);
        };
        for (let k = 0; k < rows; k++)
            for (let j = 0; j < 2; j++) {
                const a = grid[k][j], bb = grid[k][j + 1], cc = grid[k + 1][j], d = grid[k + 1][j + 1];
                const u0 = k / rows, u1 = (k + 1) / rows;
                emit(a, u0); emit(cc, u1); emit(bb, u0);
                emit(bb, u0); emit(cc, u1); emit(d, u1);
            }
    }
    return geometry(b);
}

/** Arching fern: fronds rise and droop, edges stepped into leaflets. */
function fernGeometry(fronds: number, segments: number) {
    const b = builder(), p = new T.Vector3(), n = new T.Vector3(), c = new T.Color();
    const up = new T.Vector3(0, 1, 0);
    for (let f = 0; f < fronds; f++) {
        const a = f / fronds * Math.PI * 2 + (f % 2) * .3, d = new T.Vector3(Math.cos(a), 0, Math.sin(a)), s = new T.Vector3(-d.z, 0, d.x);
        const len = .85 + (f % 3) * .12, rise = .75 + (f % 2) * .2;
        const at = (u: number) => d.clone().multiplyScalar(len * u).addScaledVector(up, len * (rise * u - (rise + .25) * u * u));
        const rows: T.Vector3[][] = [];
        for (let k = 0; k <= segments; k++) {
            const u = k / segments, w = .17 * Math.sin(Math.PI * Math.min(1, u * 1.05)) ** .6 * (k % 2 ? 1 : .62);
            const q = at(u);
            rows.push([q.clone().addScaledVector(s, -w).addScaledVector(up, -w * .25), q.clone().addScaledVector(up, .02), q.clone().addScaledVector(s, w).addScaledVector(up, -w * .25)]);
        }
        for (let k = 0; k < segments; k++)
            for (let j = 0; j < 2; j++) {
                const quad = [rows[k][j], rows[k + 1][j], rows[k][j + 1], rows[k][j + 1], rows[k + 1][j], rows[k + 1][j + 1]];
                quad.forEach((q, qi) => {
                    const u = (qi === 1 || qi === 4 || qi === 5 ? k + 1 : k) / segments;
                    n.copy(up).multiplyScalar(.7).addScaledVector(d, .3 * (u - .3)).normalize();
                    shade(.25 + u * .8, c);
                    p.copy(q);
                    vert(b, p, n, c);
                });
            }
    }
    return geometry(b);
}

/** Five-petal blossom facing up; the instance colour tints the petals, the heart stays golden. */
function flowerGeometry() {
    const b = builder(), n = new T.Vector3(0, 1, 0), petal = new T.Color(1, 1, 1), heart = new T.Color(1.15, .95, .45);
    const center = new T.Vector3(0, .04, 0);
    for (let i = 0; i < 5; i++) {
        const a = i / 5 * Math.PI * 2, l = new T.Vector3(Math.cos(a - .32) * .55, .02, Math.sin(a - .32) * .55), tip = new T.Vector3(Math.cos(a), .12, Math.sin(a)), r = new T.Vector3(Math.cos(a + .32) * .55, .02, Math.sin(a + .32) * .55);
        vert(b, center, n, heart); vert(b, tip, n, petal); vert(b, l, n, petal);
        vert(b, center, n, heart); vert(b, r, n, petal); vert(b, tip, n, petal);
    }
    return geometry(b);
}

/** Short leafy curtain hanging from a crown: clearly leaves, never a clean catchable line. */
function hangGeometry(leaves: number) {
    const b = builder(), n = new T.Vector3(), c = new T.Color();
    for (let i = 0; i < leaves; i++) {
        const u = i / (leaves - 1), y = -u, x = Math.sin(u * 2.4) * .08, side = i % 2 ? 1 : -1;
        const stem = new T.Vector3(x, y, 0), tip = new T.Vector3(x + side * .16, y - .1, .05 * side), l = new T.Vector3(x + side * .07, y - .02, .08), r = new T.Vector3(x + side * .1, y - .12, -.06);
        n.set(side * .3, .2, 1).normalize();
        shade(.75 - u * .5, c);
        vert(b, stem, n, c); vert(b, l, n, c); vert(b, tip, n, c);
        vert(b, stem, n, c); vert(b, tip, n, c); vert(b, r, n, c);
    }
    return geometry(b);
}

/** Palm crown: long arching fronds with stepped leaflets (tropical accent silhouettes). */
function palmGeometry(fronds: number) {
    const b = builder(), n = new T.Vector3(), c = new T.Color(), up = new T.Vector3(0, 1, 0);
    for (let f = 0; f < fronds; f++) {
        const a = f / fronds * Math.PI * 2, d = new T.Vector3(Math.cos(a), 0, Math.sin(a)), s = new T.Vector3(-d.z, 0, d.x);
        const lift = f % 2 ? .55 : .3;
        const at = (u: number) => d.clone().multiplyScalar(u).addScaledVector(up, lift * u - (lift + .55) * u * u);
        const segs = 7;
        for (let k = 0; k < segs; k++) {
            const u0 = k / segs, u1 = (k + 1) / segs, w = .26 * Math.sin(Math.PI * Math.min(1, u1 * 1.1)) ** .5;
            const p0 = at(u0), p1 = at(u1);
            for (const side of [-1, 1]) {
                const leaflet = p0.clone().lerp(p1, .4).addScaledVector(s, side * w).addScaledVector(up, -w * .35);
                n.copy(up).multiplyScalar(.8).addScaledVector(s, side * .2).normalize();
                shade(.45 + u1 * .5, c);
                vert(b, p0, n, c); vert(b, side > 0 ? leaflet : p1, n, c); vert(b, side > 0 ? p1 : leaflet, n, c);
            }
        }
    }
    return geometry(b);
}

/** Bromeliad rosette (epiphyte on limbs): stiff strap leaves arching out of a cup. */
function bromeliadGeometry(leaves: number) {
    const b = builder(), n = new T.Vector3(), c = new T.Color(), up = new T.Vector3(0, 1, 0);
    for (let f = 0; f < leaves; f++) {
        const a = f * 2.39996, d = new T.Vector3(Math.cos(a), 0, Math.sin(a)), s = new T.Vector3(-d.z, 0, d.x);
        const len = .75 + (f % 3) * .12, lift = 1.1 - (f % 4) * .12;
        const at = (u: number) => d.clone().multiplyScalar(len * u * (.35 + .65 * u)).addScaledVector(up, len * (lift * u - (lift + .1) * u * u * .9));
        const segs = 4;
        for (let k = 0; k < segs; k++) {
            const u0 = k / segs, u1 = (k + 1) / segs, w0 = .11 * (1 - u0 * .75), w1 = .11 * (1 - u1 * .75);
            const p0 = at(u0), p1 = at(u1);
            const q = [p0.clone().addScaledVector(s, -w0), p0.clone().addScaledVector(s, w0), p1.clone().addScaledVector(s, -w1), p1.clone().addScaledVector(s, w1)];
            n.copy(up).multiplyScalar(.75).addScaledVector(d, .25 + u0 * .3).normalize();
            // Bright crimson-to-green heart, then glossy green (the tint multiplies both).
            const tone = (u: number) => u < .3 ? c.setRGB(1.25, .55, .5) : shade(.55 + u * .45, c);
            for (const [qi, u] of [[0, u0], [2, u1], [1, u0], [1, u0], [2, u1], [3, u1]] as const) { tone(u); vert(b, q[qi], n, c); }
        }
    }
    return geometry(b);
}

/** Giant "elephant ear" leaves on arching stalks (understory, Bible §8 Jungle Floor). */
function giantLeafGeometry(count: number) {
    const b = builder(), n = new T.Vector3(), c = new T.Color(), up = new T.Vector3(0, 1, 0);
    for (let f = 0; f < count; f++) {
        const a = f / count * Math.PI * 2 + (f % 2) * .4, d = new T.Vector3(Math.cos(a), 0, Math.sin(a)), s = new T.Vector3(-d.z, 0, d.x);
        const stalk = .55 + (f % 3) * .18, tilt = .55 + (f % 2) * .25;
        const base = d.clone().multiplyScalar(stalk * .55).addScaledVector(up, stalk);
        // Stalk: a thin ribbon so the leaf reads as growing, not floating.
        const sw = .025;
        for (const [p, q] of [[new T.Vector3(), base]] as const) {
            n.copy(s);
            shade(.2, c);
            const quad = [p.clone().addScaledVector(s, -sw), p.clone().addScaledVector(s, sw), q.clone().addScaledVector(s, -sw), q.clone().addScaledVector(s, sw)];
            for (const qi of [0, 2, 1, 1, 2, 3]) vert(b, quad[qi], n, c);
        }
        // Heart-shaped blade: rows across, a midrib crease, the tip drooping.
        const rows = 5, L = .95 + (f % 2) * .2;
        const along = (u: number) => base.clone().addScaledVector(d, L * u * Math.cos(tilt * (1 - u * .6))).addScaledVector(up, L * (Math.sin(tilt) * u * .55 - u * u * .45));
        const width = (u: number) => .42 * L * Math.sin(Math.PI * Math.min(1, .15 + u * .95)) ** .7;
        for (let k = 0; k < rows; k++) {
            const u0 = k / rows, u1 = (k + 1) / rows, p0 = along(u0), p1 = along(u1), w0 = width(u0), w1 = width(u1);
            const grid = [[p0.clone().addScaledVector(s, -w0).addScaledVector(up, -.06), p0.clone(), p0.clone().addScaledVector(s, w0).addScaledVector(up, -.06)],
                [p1.clone().addScaledVector(s, -w1).addScaledVector(up, -.06), p1.clone(), p1.clone().addScaledVector(s, w1).addScaledVector(up, -.06)]];
            for (let j = 0; j < 2; j++) {
                const quad = [grid[0][j], grid[0][j + 1], grid[1][j], grid[1][j + 1]];
                n.copy(up).multiplyScalar(.8).addScaledVector(s, j ? .25 : -.25).addScaledVector(d, -.2).normalize();
                for (const qi of [0, 2, 1, 1, 2, 3]) { shade(.5 + (qi > 1 ? u1 : u0) * .45 + (qi % 2 === j % 2 ? .08 : 0), c); vert(b, quad[qi], n, c); }
            }
        }
    }
    return geometry(b);
}

/** Water-lily pads: flat notched discs floating in a loose cluster. */
function lilyGeometry(pads: number) {
    const b = builder(), n = new T.Vector3(0, 1, 0), c = new T.Color(), center = new T.Vector3();
    for (let i = 0; i < pads; i++) {
        const a = i * 2.39996, r = i ? .5 + (i % 3) * .28 : 0, R = .32 + (i % 2) * .14;
        center.set(Math.cos(a) * r, .01 * (i % 2), Math.sin(a) * r);
        const notch = a * 1.7, seg = 9;
        for (let k = 0; k < seg; k++) {
            const t0 = notch + .35 + k / seg * (Math.PI * 2 - .7), t1 = notch + .35 + (k + 1) / seg * (Math.PI * 2 - .7);
            shade(.75, c);
            vert(b, center, n, c);
            shade(.6 + .1 * (k % 2), c);
            vert(b, new T.Vector3(center.x + Math.cos(t1) * R, center.y, center.z + Math.sin(t1) * R), n, c);
            vert(b, new T.Vector3(center.x + Math.cos(t0) * R, center.y, center.z + Math.sin(t0) * R), n, c);
        }
    }
    return geometry(b);
}

/** Hanging moss beard: a few ragged strands, clearly fibrous (never a clean catchable line). */
function beardGeometry(strands: number) {
    const b = builder(), n = new T.Vector3(), c = new T.Color();
    for (let i = 0; i < strands; i++) {
        const a = i * 2.39996, ox = Math.cos(a) * .18 * (i % 3), oz = Math.sin(a) * .18 * (i % 3), len = .55 + (i % 4) * .15;
        for (let k = 0; k < 5; k++) {
            const u0 = k / 5, u1 = (k + 1) / 5, w = .09 * (1 - u0 * .6);
            const sway = (u: number) => Math.sin(u * 3 + i) * .05;
            const p0 = new T.Vector3(ox + sway(u0), -len * u0, oz), p1 = new T.Vector3(ox + sway(u1), -len * u1, oz);
            n.set(Math.cos(a), .2, Math.sin(a)).normalize();
            shade(.8 - u0 * .4, c);
            const side = new T.Vector3(-Math.sin(a), 0, Math.cos(a));
            const quad = [p0.clone().addScaledVector(side, -w), p0.clone().addScaledVector(side, w), p1.clone().addScaledVector(side, -w * .7), p1.clone().addScaledVector(side, w * .7)];
            for (const qi of [0, 2, 1, 1, 2, 3]) vert(b, quad[qi], n, c);
        }
    }
    return geometry(b);
}

type Lod = { high: T.BufferGeometry; low: T.BufferGeometry };
let lods: Record<FoliageKind, Lod> | null = null;
/** Geometry library, built once and shared by every region and rebuild. */
export function foliageGeometry() {
    if (lods) return lods;
    const clumpA = clumpGeometry(11, 20, 1.02), clumpB = clumpGeometry(29, 18, 1.08);
    lods = {
        clump: { high: clumpA, low: clumpGeometry(11, 9, 1.35) },
        clumpB: { high: clumpB, low: clumpGeometry(29, 8, 1.4) },
        fern: { high: fernGeometry(7, 8), low: fernGeometry(5, 4) },
        flower: { high: flowerGeometry(), low: flowerGeometry() },
        hang: { high: hangGeometry(9), low: hangGeometry(5) },
        palm: { high: palmGeometry(9), low: palmGeometry(7) },
        far: { high: clumpGeometry(41, 0), low: clumpGeometry(41, 0) },
        bromeliad: { high: bromeliadGeometry(11), low: bromeliadGeometry(6) },
        giant: { high: giantLeafGeometry(5), low: giantLeafGeometry(3) },
        lily: { high: lilyGeometry(6), low: lilyGeometry(3) },
        beard: { high: beardGeometry(7), low: beardGeometry(4) },
    };
    return lods;
}

let foliageMaterial: T.MeshStandardMaterial | null = null;
/** One foliage material for every clump, fern, flower and hanging plant. */
export function foliageMat() {
    if (foliageMaterial) return foliageMaterial;
    foliageMaterial = new T.MeshStandardMaterial({ vertexColors: true, roughness: .88, metalness: 0, side: T.DoubleSide });
    stylize(foliageMaterial, { fade: 'cluster', sway: true, foliage: true });
    foliageMaterial.userData.shared = true;
    return foliageMaterial;
}
let farMaterial: T.MeshStandardMaterial | null = null;
/** Distant treelines: no scene fog, a capped haze so layered silhouettes survive (Bible §3). */
export function farFoliageMat() {
    if (farMaterial) return farMaterial;
    farMaterial = new T.MeshStandardMaterial({ vertexColors: true, roughness: 1, fog: false });
    stylize(farMaterial, { grade: true, haze: .72 });
    farMaterial.userData.shared = true;
    return farMaterial;
}

const tmp = new T.Object3D();
/** Instance collector; `build` emits one InstancedMesh per kind with high/low LOD geometry. */
export class Scatter {
    items = new Map<FoliageKind, { matrix: T.Matrix4; color: T.Color }[]>();
    /** Clump requests use this one variant, so each cell costs a single clump draw call. */
    constructor(private clump: 'clump' | 'clumpB' = 'clump') { }
    add(kind: FoliageKind, position: T.Vector3Like, scale: number | T.Vector3Like, yaw: number, color: T.ColorRepresentation, tilt = 0) {
        if (kind === 'clump' || kind === 'clumpB') kind = this.clump;
        tmp.position.set(position.x, position.y, position.z);
        tmp.rotation.set(tilt, yaw, 0, 'YXZ');
        if (typeof scale === 'number') tmp.scale.setScalar(scale);
        else tmp.scale.set(scale.x, scale.y, scale.z);
        tmp.updateMatrix();
        if (!this.items.has(kind)) this.items.set(kind, []);
        this.items.get(kind)!.push({ matrix: tmp.matrix.clone(), color: new T.Color(color) });
    }
    get count() { let n = 0; for (const list of this.items.values()) n += list.length; return n; }
    build(material = foliageMat()) {
        const group = new T.Group(), library = foliageGeometry();
        for (const [kind, list] of this.items) {
            if (!list.length) continue;
            const lod = library[kind];
            const mesh = new T.InstancedMesh(lod.high, kind === 'far' ? farFoliageMat() : material, list.length);
            list.forEach((item, i) => { mesh.setMatrixAt(i, item.matrix); mesh.setColorAt(i, item.color); });
            mesh.instanceMatrix.needsUpdate = true;
            if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
            mesh.computeBoundingSphere();
            mesh.userData.highGeometry = lod.high;
            mesh.userData.lowGeometry = lod.low;
            if (kind === 'clump' || kind === 'clumpB') mesh.userData.farGeometry = library.far.high;
            mesh.userData.sharedGeometry = true;
            mesh.castShadow = kind === 'clump' || kind === 'clumpB' || kind === 'palm';
            mesh.receiveShadow = kind !== 'far';
            mesh.name = `foliage-${kind}`;
            group.add(mesh);
        }
        return group;
    }
}
